import { type NodeProps, Position } from '@xyflow/react';
import EditIcon from '@mui/icons-material/Edit';
import { v4 as uuidv4 } from 'uuid';

import { type NodeItem, type ErrorNodeData, type ParentNode } from '@models/flows';
import { Connector } from '@utils/flows';

import { useToast } from '@hooks/useToast';
import { useNodeError } from '@hooks/useNodeError';

import {
  AddButton,
  ConnectionPoint,
  DeleteButton,
  ErrorToolTip,
} from '@components/Flows/components/partials/index';
import { initialErrorNodeData } from '@features/chargerFlows/validation/validation-helpers';

import s from './ErrorNode.module.css';

type ErrorBlockProps = {
  nodeItem: NodeItem;
  onEdit: (code: string) => void;
};

const ErrorBlock = ({ nodeItem: { label, id }, onEdit }: ErrorBlockProps) => (
  <div className={s.errorBlock}>
    <div className={s.errorLineWrapper}>
      <p className={s.errorLabel}>{label}</p>
    </div>
    <button type="button" className={s.editButton} onClick={() => onEdit(id)}>
      <EditIcon />
    </button>
  </div>
);

type ErrorNodeProps = NodeProps & {
  onEdit: (parentNode: ParentNode, childNode: NodeItem) => void;
  onChange: (parentNode: ParentNode, childNode: NodeItem) => void;
  onDelete: (nodeId: string) => void;
};

const getNewErrorNodeItem = (): NodeItem => ({ id: uuidv4(), code: '', label: '' });

function ErrorNode({ id, type, data, onEdit, onChange, onDelete }: ErrorNodeProps) {
  const toast = useToast();
  const errorNodeData = data as unknown as ErrorNodeData;
  const nodeItems = errorNodeData?.items || [initialErrorNodeData];
  const nodeId = id;
  const parentNode: ParentNode = {
    id: nodeId,
    type,
    data: errorNodeData,
  };
  const errors = useNodeError(nodeId);

  const handleAddNewErrorNodeItem = () => {
    onChange(parentNode, getNewErrorNodeItem());
  };

  const handleNodeItemEdit = (id: string) => {
    const editableItem = nodeItems.find((item) => item.id === id);

    if (!editableItem) {
      toast.error('Error item not found');
      return;
    }

    onEdit(parentNode, editableItem);
  };

  return (
    <div className={`${s.errorContainer} ${errors ? s.errorBorder : ''}`}>
      <h1 className={s.title}>Error Node</h1>
      <ErrorToolTip isErrorVisible={Boolean(errors)} messages={errors?.messages} />
      <DeleteButton onDelete={() => onDelete(nodeId)} />
      <div className={s.errorBlockList}>
        {nodeItems &&
          nodeItems.map((error: NodeItem, index: number) => (
            <ErrorBlock key={index} nodeItem={error} onEdit={handleNodeItemEdit} />
          ))}
      </div>
      <AddButton handleClick={handleAddNewErrorNodeItem} />
      <ConnectionPoint type={Connector.Source} position={Position.Right} id="outgoing" />
      <ConnectionPoint type={Connector.Source} position={Position.Bottom} id="negative" />
    </div>
  );
}

export default ErrorNode;
